// Tuition row identity, matching, suggestions, and provider-facing calculations.
import { v4 as uuidv4 } from 'uuid';
import { getAgeFromBirthday, toDateObject } from '../dates';
import { convertToDollars } from '../money';
import { ANNUAL_CHARGE_COUNTS } from './cadence';
import { convertBillingIntervalAmount, convertCadenceCents, dollarsToCents } from './financial';

export const WEEKLY_DIVISOR = 4.33;
export const BIWEEKLY_DIVISOR = 2.165;
export const TWICE_PER_MONTH_DIVISOR = 2;

export type TuitionPriceOption = {
  hidden?: boolean;
  daysPerWeek?: number | null;
  monthlyRate?: number | null;
  weeklyRate?: number | null;
};

export type TuitionProgram = {
  name?: string;
  ageMonthsMin?: number | null;
  ageMonthsMax?: number | null;
  priceOptions?: TuitionPriceOption[] | null;
};

export type SuggestedStudent = {
  birthday?: any;
  displayName?: string;
  enrollment?: { scheduledDays?: unknown[] | null } | null;
  firstName?: string;
  lastName?: string;
  schedule?: unknown[] | null;
};

export type TuitionCalculation = {
  daysPerWeek: number | null;
  hasTuitionSetting: boolean;
  intervalAmount: number;
  previousHasTuitionSetting: boolean;
  previousIntervalAmount: number;
  sourceAmount: number;
  sourcePeriod: 'month' | 'week';
  studentName: string;
  tuitionSettingIntervalAmount: number | null;
  tuitionSettingState: TuitionSettingState;
  invoiceItemId?: string | null;
  invoiceItemIndex?: number;
  studentId?: string | null;
  baselineAmountCents?: number;
  convertedAmountCents?: number;
  currentAmountCents?: number;
};

export type TuitionSettingState =
  | 'configured'
  | 'empty'
  | 'missingRate'
  | 'noMatchingProgram'
  | 'unavailable'
  | 'zeroRate';

export type ExistingTuitionItem = {
  amount?: number | string | null;
  discounts?: Array<{ amount?: number | string | null; amountType?: string | null }> | null;
  notes?: string | null;
};

export function buildTuitionCalculations(
  students?: SuggestedStudent[] | null,
  tuitionPrograms?: TuitionProgram[] | null,
  billingInterval?: string,
  previousBillingInterval?: string,
  existingTuitionItems: ExistingTuitionItem[] = [],
  tuitionSettingsAvailable = true
): TuitionCalculation[] {
  if (!students?.length) return [];

  const programs = tuitionPrograms ?? [];

  return students.map((student, studentIndex): TuitionCalculation => {
    const ageMonths = getStudentAgeMonths(student);
    const program = findMatchingProgram(programs, ageMonths);
    const scheduledDaysPerWeek = getStudentDaysPerWeek(student);
    const rate = program ? getBestRateForInterval(program, billingInterval, scheduledDaysPerWeek) : null;
    const previousRate =
      program && previousBillingInterval
        ? getBestRateForInterval(program, previousBillingInterval, scheduledDaysPerWeek)
        : null;
    const studentName = student.displayName || `${student.firstName || ''} ${student.lastName || ''}`.trim();
    const existingTuitionItem =
      existingTuitionItems.find((item) => item.notes && item.notes === studentName) ??
      existingTuitionItems[studentIndex];
    const existingAmount = Number(existingTuitionItem?.amount);
    const previousIntervalAmount = Number.isFinite(existingAmount)
      ? existingAmount
      : previousRate
        ? Number(convertToDollars(previousRate.intervalRateCents).toFixed(2))
        : 0;
    const calculatedIntervalAmount = convertBillingIntervalAmount(
      previousIntervalAmount,
      previousBillingInterval,
      billingInterval
    );

    const tuitionSettingState = getTuitionSettingState({
      program,
      programs,
      rate,
      tuitionSettingsAvailable,
    });

    if (tuitionSettingState !== 'configured' || !rate) {
      return {
        daysPerWeek: scheduledDaysPerWeek,
        hasTuitionSetting: false,
        intervalAmount: calculatedIntervalAmount ?? 0,
        previousHasTuitionSetting: calculatedIntervalAmount != null,
        previousIntervalAmount,
        sourceAmount: 0,
        sourcePeriod: billingInterval === 'Weekly' || billingInterval === 'Bi-Weekly' ? 'week' : 'month',
        studentName,
        baselineAmountCents: dollarsToCents(previousIntervalAmount),
        convertedAmountCents: dollarsToCents(calculatedIntervalAmount ?? 0),
        currentAmountCents: dollarsToCents(calculatedIntervalAmount ?? 0),
        tuitionSettingIntervalAmount: null,
        tuitionSettingState,
      };
    }

    return {
      daysPerWeek: rate.daysPerWeek,
      hasTuitionSetting: true,
      // An interval edit must not replace a family's negotiated rate with today's
      // Tuition Settings. Preserve the existing annual tuition and use Settings
      // only as a visible comparison.
      intervalAmount: calculatedIntervalAmount ?? 0,
      previousHasTuitionSetting: calculatedIntervalAmount != null,
      previousIntervalAmount,
      sourceAmount: Number(convertToDollars(rate.sourceRateCents).toFixed(2)),
      sourcePeriod: rate.sourcePeriod,
      studentName,
      baselineAmountCents: dollarsToCents(previousIntervalAmount),
      convertedAmountCents: dollarsToCents(calculatedIntervalAmount ?? 0),
      currentAmountCents: dollarsToCents(calculatedIntervalAmount ?? 0),
      tuitionSettingIntervalAmount: Number(convertToDollars(rate.intervalRateCents).toFixed(2)),
      tuitionSettingState,
    };
  });
}

export function buildTuitionCalculationsForMatches(
  matches: Array<
    TuitionItemMatch & { baselineInvoiceItem?: TuitionInvoiceItem; currentInvoiceItem?: TuitionInvoiceItem }
  >,
  tuitionPrograms?: TuitionProgram[] | null,
  billingInterval?: string,
  previousBillingInterval?: string,
  tuitionSettingsAvailable = true
): TuitionCalculation[] {
  return matches.map(({ invoiceItem, baselineInvoiceItem, currentInvoiceItem, invoiceItemIndex, student }) => {
    const baseline = baselineInvoiceItem ?? invoiceItem;
    const current = currentInvoiceItem;
    const calculation = buildTuitionCalculations(
      [student],
      tuitionPrograms,
      billingInterval,
      previousBillingInterval,
      [baseline],
      tuitionSettingsAvailable
    )[0];
    const currentAmountCents = current ? dollarsToCents(current.amount) : (calculation.convertedAmountCents ?? 0);
    return {
      ...calculation,
      intervalAmount: currentAmountCents / 100,
      currentAmountCents,
      invoiceItemId: baseline.id ?? null,
      invoiceItemIndex,
      studentId: student.id ?? student.uid ?? null,
    };
  });
}

function getTuitionSettingState({
  program,
  programs,
  rate,
  tuitionSettingsAvailable,
}: {
  program: TuitionProgram | null | undefined;
  programs: TuitionProgram[];
  rate: ReturnType<typeof getBestRateForInterval>;
  tuitionSettingsAvailable: boolean;
}): TuitionSettingState {
  if (!tuitionSettingsAvailable) return 'unavailable';
  if (!programs.length) return 'empty';
  if (!program) return 'noMatchingProgram';
  if (!rate) return 'missingRate';
  if (rate.intervalRateCents <= 0) return 'zeroRate';
  return 'configured';
}

export type SuggestedInvoiceItem = {
  id: string;
  category: string;
  item: string;
  notes: string;
  amount: number;
  discounts: never[];
};

// Preserve the existing new-plan behavior independently from interval changes on
// saved plans. Existing or manually entered rows still win in the form.
export function buildSuggestedItems(
  students?: SuggestedStudent[] | null,
  tuitionPrograms?: TuitionProgram[] | null,
  billingInterval?: string
): SuggestedInvoiceItem[] {
  if (!students?.length || !tuitionPrograms?.length) return [];

  const items: SuggestedInvoiceItem[] = [];
  for (const student of students) {
    const ageMonths = getStudentAgeMonths(student);
    const program = findMatchingProgram(tuitionPrograms, ageMonths);
    if (!program) continue;

    const monthlyRateCents = getBestMonthlyRate(program);
    if (monthlyRateCents == null) continue;

    const amount = computeAmount(convertToDollars(monthlyRateCents), billingInterval);

    items.push({
      id: uuidv4(),
      category: 'Tuition',
      item: buildAgeLabel(program),
      notes: student.displayName || `${student.firstName || ''} ${student.lastName || ''}`.trim(),
      amount: Number(amount.toFixed(2)),
      discounts: [],
    });
  }

  return items;
}

export function getBestMonthlyRate(program: TuitionProgram) {
  const options = (program.priceOptions ?? []).filter((option) => !option.hidden);
  if (!options.length) return null;

  const sorted = [...options].sort((a, b) => (b.daysPerWeek ?? 0) - (a.daysPerWeek ?? 0));
  for (const option of sorted) {
    const monthlyRate = option.monthlyRate ?? null;
    if (monthlyRate != null) return monthlyRate;

    const weeklyRate = option.weeklyRate ?? null;
    if (weeklyRate != null) return weeklyRate * WEEKLY_DIVISOR;
  }
  return null;
}

export function computeAmount(monthlyRate: number, billingInterval?: string) {
  switch (billingInterval) {
    case 'Weekly':
      return monthlyRate / WEEKLY_DIVISOR;
    case 'Bi-Weekly':
      return monthlyRate / BIWEEKLY_DIVISOR;
    case 'Twice Per Month':
      return monthlyRate / TWICE_PER_MONTH_DIVISOR;
    default:
      return monthlyRate;
  }
}

export function buildAgeLabel(program: TuitionProgram) {
  const min = program.ageMonthsMin;
  const max = program.ageMonthsMax;
  if (min == null && max == null) return program.name || 'Tuition';
  if (max == null) return `${min}+ months`;
  return `${min} - ${max} months`;
}

export type TuitionInvoiceItem = ExistingTuitionItem & {
  id?: string | null;
  category?: string | null;
  item?: string | null;
  student?: { id?: string | null; uid?: string | null } | null;
  studentId?: string | null;
  studentUid?: string | null;
};

type MatchableStudent = SuggestedStudent & { id?: string | null; uid?: string | null };

export type TuitionItemMatch = {
  invoiceItem: TuitionInvoiceItem;
  invoiceItemIndex: number;
  student: MatchableStudent;
  studentIndex: number;
};

export type TuitionItemMatchResult = {
  matches: TuitionItemMatch[];
  status: 'ambiguous' | 'matched' | 'none';
};

// Interval transitions use stable identity only. Legacy labels, notes,
// translated descriptions, and array position are not durable financial
// ownership metadata.
export function matchTuitionInvoiceItems(
  invoiceItems: TuitionInvoiceItem[] = [],
  students: MatchableStudent[] = []
): TuitionItemMatchResult {
  const candidates = invoiceItems
    .map((invoiceItem, invoiceItemIndex) => ({ invoiceItem, invoiceItemIndex }))
    .filter(({ invoiceItem }) => invoiceItem.category === 'Tuition');

  if (!candidates.length) return { matches: [], status: 'none' };
  if (!students.length) return { matches: [], status: 'ambiguous' };

  const matches: TuitionItemMatch[] = [];

  for (let studentIndex = 0; studentIndex < students.length; studentIndex += 1) {
    const student = students[studentIndex];
    const strongMatches = candidates.filter(({ invoiceItem }) => invoiceItemMatchesStudent(invoiceItem, student));

    matches.push(...strongMatches.map((match) => ({ ...match, student, studentIndex })));
  }

  // Every Tuition row must independently resolve to exactly one current student.
  // Multiple rows may intentionally belong to the same student, but text and row
  // position never establish identity.
  const matchedIndexes = matches.map(({ invoiceItemIndex }) => invoiceItemIndex);
  if (matches.length === candidates.length && new Set(matchedIndexes).size === candidates.length) {
    return { matches, status: 'matched' };
  }

  return { matches: [], status: 'ambiguous' };
}

function invoiceItemMatchesStudent(invoiceItem: TuitionInvoiceItem, student: MatchableStudent) {
  const studentIds = [student.id, student.uid].filter(Boolean);
  const invoiceItemStudentIds = [
    invoiceItem.studentId,
    invoiceItem.studentUid,
    invoiceItem.student?.id,
    invoiceItem.student?.uid,
  ].filter(Boolean);
  return studentIds.some((studentId) => invoiceItemStudentIds.includes(studentId));
}

export function getStudentAgeMonths(student: SuggestedStudent) {
  const birthday = toDateObject(student.birthday);
  const age = getAgeFromBirthday(birthday);
  return age?.totalMonths ?? null;
}

export function getStudentDaysPerWeek(student: SuggestedStudent) {
  const scheduledDays = student.enrollment?.scheduledDays;
  if (Array.isArray(scheduledDays) && scheduledDays.length) return scheduledDays.length;
  if (Array.isArray(student.schedule) && student.schedule.length) return student.schedule.length;
  return null;
}

export function findMatchingProgram(tuitionPrograms: TuitionProgram[], ageMonths: number | null) {
  if (ageMonths === null) return null;
  return tuitionPrograms.find((p) => {
    const min = p.ageMonthsMin ?? 0;
    const max = p.ageMonthsMax ?? 9999;
    return ageMonths >= min && ageMonths <= max;
  });
}

export function getBestRateForInterval(
  program: TuitionProgram,
  billingInterval?: string,
  scheduledDaysPerWeek?: number | null
) {
  const visibleOptions = (program.priceOptions ?? []).filter((option) => !option.hidden);
  const options = scheduledDaysPerWeek
    ? visibleOptions.filter((option) => (option.daysPerWeek ?? null) === scheduledDaysPerWeek)
    : [...visibleOptions].sort((a, b) => (b.daysPerWeek ?? 0) - (a.daysPerWeek ?? 0));

  for (const option of options) {
    if (billingInterval === 'Weekly' || billingInterval === 'Bi-Weekly') {
      const weeklyRate = option.weeklyRate ?? null;
      if (weeklyRate == null) continue;
      return {
        daysPerWeek: option.daysPerWeek ?? null,
        intervalRateCents: billingInterval === 'Bi-Weekly' ? weeklyRate * 2 : weeklyRate,
        sourcePeriod: 'week' as const,
        sourceRateCents: weeklyRate,
      };
    }

    const monthlyRate = option.monthlyRate ?? null;
    if (monthlyRate == null) continue;
    return {
      daysPerWeek: option.daysPerWeek ?? null,
      intervalRateCents: billingInterval === 'Twice Per Month' ? monthlyRate / 2 : monthlyRate,
      sourcePeriod: 'month' as const,
      sourceRateCents: monthlyRate,
    };
  }

  return null;
}

type TranslateFn = (key: string, options?: any) => string;

export type TuitionConversionSummary = {
  after: string;
  before: string;
  calculation: string;
  daysPerWeek: number | null;
  differenceCalculation: string | null;
  hasTuitionSetting: boolean;
  studentName: string;
  tuitionSettingAmount: string | null;
  tuitionSettingState: TuitionSettingState;
  invoiceItemId?: string | null;
  invoiceItemIndex?: number;
  studentId?: string | null;
};

export function buildTuitionConversionSummaries(
  calculations: TuitionCalculation[] | null | undefined,
  billingInterval: string | undefined,
  previousBillingInterval: string | undefined,
  t: TranslateFn
): TuitionConversionSummary[] {
  if (!calculations?.length || !billingInterval || !previousBillingInterval) return [];
  if (billingInterval === previousBillingInterval) return [];

  const previousAnnualCount = ANNUAL_CHARGE_COUNTS[previousBillingInterval.toLowerCase()];
  const annualCount = ANNUAL_CHARGE_COUNTS[billingInterval.toLowerCase()];
  if (previousAnnualCount == null || annualCount == null) return [];

  const previousPeriod = getTuitionPeriodLabel(previousBillingInterval, t);
  const period = getTuitionPeriodLabel(billingInterval, t);

  return calculations
    .filter((calculation) => calculation.previousHasTuitionSetting)
    .map((calculation) => {
      const baselineAmountCents = calculation.baselineAmountCents ?? dollarsToCents(calculation.previousIntervalAmount);
      const convertedAmountCents =
        calculation.convertedAmountCents ??
        convertCadenceCents(baselineAmountCents, previousBillingInterval, billingInterval);
      const currentAmountCents = calculation.currentAmountCents ?? dollarsToCents(calculation.intervalAmount);
      const previousAmount = baselineAmountCents / 100;
      const convertedAmount = convertedAmountCents / 100;
      const afterAmount = currentAmountCents / 100;
      const tuitionSettingAmount = calculation.tuitionSettingIntervalAmount;
      const before = `${formatDollars(previousAmount)}/${previousPeriod}`;
      const converted = `${formatDollars(convertedAmount)}/${period}`;
      const after = `${formatDollars(afterAmount)}/${period}`;

      return {
        after,
        before,
        calculation: `${formatDollars(previousAmount)} × ${previousAnnualCount} ÷ ${annualCount} = ${converted}`,
        daysPerWeek: calculation.daysPerWeek,
        differenceCalculation: calculation.hasTuitionSetting
          ? `${formatDollars(tuitionSettingAmount ?? 0)} − ${formatDollars(afterAmount)} = ${formatSignedDollars(
              (tuitionSettingAmount ?? 0) - afterAmount
            )}/${period}`
          : null,
        hasTuitionSetting: calculation.hasTuitionSetting,
        studentName: calculation.studentName,
        invoiceItemId: calculation.invoiceItemId,
        invoiceItemIndex: calculation.invoiceItemIndex,
        studentId: calculation.studentId,
        tuitionSettingAmount: tuitionSettingAmount == null ? null : `${formatDollars(tuitionSettingAmount)}/${period}`,
        tuitionSettingState: calculation.tuitionSettingState,
      };
    });
}

// New-plan suggestions intentionally keep their pre-existing copy and monthly
// conversion rules. Saved-plan interval changes use the detailed calculation
// banner above and are a separate workflow.
export function buildSuggestedItemsMathNote(
  suggestedItems: SuggestedInvoiceItem[] | null | undefined,
  billingInterval: string | undefined,
  t: TranslateFn
) {
  if (!suggestedItems?.length) return null;
  if (billingInterval === 'Weekly') {
    return t('billing.tuitionAutofill.weeklyNote', {
      defaultValue:
        'Amounts calculated from monthly tuition ÷ 4.33 (average weeks per month). You can edit any amount below.',
    });
  }
  if (billingInterval === 'Bi-Weekly') {
    return t('billing.tuitionAutofill.biweeklyNote', {
      defaultValue:
        'Amounts calculated from monthly tuition ÷ 2.165 (average bi-weekly periods per month). You can edit any amount below.',
    });
  }
  if (billingInterval === 'Twice Per Month') {
    return t('billing.tuitionAutofill.twicePerMonthNote', {
      defaultValue:
        'Amounts calculated from monthly tuition ÷ 2 (two charges per month). You can edit any amount below.',
    });
  }
  if (billingInterval === 'Monthly') {
    return t('billing.tuitionAutofill.monthlyNote', {
      defaultValue: 'Amounts pre-filled from your tuition settings. You can edit any amount below.',
    });
  }
  return null;
}

export function buildTuitionAmountMath(
  calculation: TuitionCalculation,
  billingInterval: string | undefined,
  t: TranslateFn
) {
  const period = getTuitionPeriodLabel(billingInterval, t);
  const values = {
    daysPerWeek: String(calculation.daysPerWeek ?? ''),
    intervalAmount: formatDollars(calculation.tuitionSettingIntervalAmount ?? calculation.intervalAmount),
    period,
    sourceAmount: formatDollars(calculation.sourceAmount),
    sourcePeriod: getSourcePeriodLabel(calculation.sourcePeriod, t),
  };

  const calculationText =
    billingInterval === 'Bi-Weekly'
      ? t('billing.tuitionAutofill.biweeklyMath', {
          defaultValue: '{{sourceAmount}}/week × 2 weeks = {{intervalAmount}}/{{period}}',
          ...values,
        })
      : billingInterval === 'Twice Per Month'
        ? t('billing.tuitionAutofill.twicePerMonthMath', {
            defaultValue: '{{sourceAmount}}/month ÷ 2 = {{intervalAmount}}/{{period}}',
            ...values,
          })
        : t('billing.tuitionAutofill.directRateMath', {
            defaultValue: '{{sourceAmount}}/{{sourcePeriod}} × 1 = {{intervalAmount}}/{{period}}',
            ...values,
          });
  return calculationText;
}

function getSourcePeriodLabel(sourcePeriod: TuitionCalculation['sourcePeriod'], t: TranslateFn) {
  const billingInterval = sourcePeriod === 'week' ? 'Weekly' : 'Monthly';
  return getTuitionPeriodLabel(billingInterval, t);
}

function getTuitionPeriodLabel(billingInterval: string | undefined, t: TranslateFn) {
  const defaultValue =
    {
      Weekly: 'week',
      'Bi-Weekly': 'two weeks',
      'Twice Per Month': 'half month',
      Monthly: 'month',
    }[billingInterval ?? ''] ??
    billingInterval?.toLowerCase() ??
    '';

  return t(`billing.tuitionAutofill.period.${billingInterval ?? 'unknown'}`, { defaultValue });
}

function formatDollars(amount: number) {
  const absolute = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return amount < 0 ? `−$${absolute}` : `$${absolute}`;
}

function formatSignedDollars(amount: number) {
  if (amount < 0) return `−${formatDollars(Math.abs(amount))}`;
  if (amount > 0) return `+${formatDollars(amount)}`;
  return formatDollars(0);
}
